import { stringify as stringifyQuery, parse as parseQuery } from 'querystring'

/** Checks if the sourcesuffix is valid. */
function sourcesuffixIsValid(str) {
  // Weniger als 10 Zeichen, oder mehr als 11 Zeichen
  return str && (str.length === 10 || str.length === 11)
}

/** Checks if the cpid is valid. */
function cpidIsValid(str) {
  // Mehr oder weniger als 9 Zeichen und/oder Buchstabe enthalten
  return str && /^[0-9]{9}$/i.exec(str) !== null
}

/** Get key by value from object/map. */
function getKeyByValue(object, value) {
  return Object.keys(object).find(key => object[key] === value)
}

/** Get key by value string from object/map. */
function getKeyByValueString(object, value) {
  return Object.keys(object).find(key => object[key].indexOf(value) > -1)
}

/** Returns an object of all query parameters. */
export function getCurrentParameters() {
  const currentQuerystring = window.location.search.slice(1)
  return parseQuery(currentQuerystring)
}

/** Check if parameters include CPID (lower and capital case) */
function hasCPID(parameters) {
  const keys = Object.keys(parameters).map(parameter => parameter.toLowerCase())
  return keys.includes('cpid')
}

/** Check if parameters include multicode (lower and capital case) */
function hasMulticode(parameters) {
  const keys = Object.keys(parameters).map(parameter => parameter.toLowerCase())
  return keys.includes('multicode')
}

/** Check if parameters include Sourcesuffix (lower and capital case) */
export function hasSourcesuffix(parameters) {
  const keys = Object.keys(parameters).map(parameter => parameter.toLowerCase())
  return keys.includes('sourcesuffix')
}

export function appendToURL(url, appendage) {
  const lastChar = url.substr(-1)
  if (lastChar !== '/') {
    url = `${url}/`
  }

  return `${url}${appendage}`
}

export function getCodeEndpointURL() {
  const url = window.location.href
  const replacedURL = url.replace('zugang/', '')
  const endpointURL = `${replacedURL}code.php`
  return endpointURL
}


/** Build the card link. Pass on all parameters, but calculate new sourcesuffix. */
export function buildNavLink(currentVariant, targetVariant, path) {
  // Return when pre-rendered. Because we need the (browser) URL parameters.
  if (typeof window === 'undefined') {
    return path
  }

  // Suffix the targetVariant when on a `mr` or `aff` or `mr-aff` page.
  // `mr-aff` as first if statement since it's also true for `mr` and `aff`
  if (window.location.pathname.indexOf('aff') > -1) {
    targetVariant = `${targetVariant}-aff`
  }

  const parameters = getCurrentParameters()


  if (!currentVariant || !targetVariant) {
    const querystring = stringifyQuery(parameters)
    return `${path}/?${querystring}`
  }

  const currentVariantOptions = window.cta.tracking[currentVariant]
  const targetVariantOptions = window.cta.tracking[targetVariant]


  // calculate sourcesuffix if variant exists in gatsby-browser.js
  if (
    currentVariantOptions !== undefined &&
    targetVariantOptions !== undefined) {
    // Merge in default sourcesuffix (if not provided)
    if (
      !hasSourcesuffix(parameters)
    ) {
      parameters.sourcesuffix = targetVariantOptions.sourcesuffix.default
    }

    if (!currentVariantOptions) {
      const querystring = stringifyQuery(parameters)
      return `${path}/?${querystring}`
    }

    // Calculate new sourcesuffix.
    // If sourcesuffix type is one of media, ppc or affiliate, use the corresponding card sourcesuffix type.
    // Search the current sourcesuffix inside the currentVariantOptions. Find the type (media, ppc, affiliate) and get the corresponding sourcesuffix from the targetVariantOptions.
    const sourcesuffixType = Object.keys(currentVariantOptions.sourcesuffix).find(
      key => currentVariantOptions.sourcesuffix[key] === parameters.sourcesuffix
    )

    const newTargetSourcesuffix =
      targetVariantOptions.sourcesuffix[sourcesuffixType]

    if (newTargetSourcesuffix) {
      parameters.sourcesuffix = newTargetSourcesuffix
    }

  }

  const querystring = stringifyQuery(parameters)

  // `stringifyQuery` encodes symbols, e.g. `:` into `%3A`. This leads to problems where the actual : is needed for tracking.
  // To test things out, we only convert back in the blue version.
  const fixedQuerystring = querystring.replace(/%3A/g, ':')

  return `${path}/?${fixedQuerystring}`
}

/** Build CTA URLs. */
export function buildCTA(
  targetVariant, // e.g. angebot-gold
  multicard = false,
  stringSearch = false
) {
  // Return when pre-rendered. Because we need the (browser) URL parameters.
  if (typeof window === 'undefined') {
    return ''
  }

  const targetVariantOptions = window.cta.tracking[targetVariant]
  const currentParameters = getCurrentParameters()

  // Merge in (optional) targetVariantOptions from gatsby-browser.js.
  const parameters = targetVariantOptions
    ? { ...targetVariantOptions.static, ...currentParameters }
    : currentParameters

  if (multicard) {
    // Find respective sourcesuffix based on type (media, ppc, affiliate)
    const type = stringSearch
      ? getKeyByValueString(
        window.cta.tracking.multicardString,
        parameters.sourcesuffix
      )
      : getKeyByValue(window.cta.tracking.multicard, parameters.sourcesuffix)
    if (type) {
      parameters.sourcesuffix = targetVariantOptions.sourcesuffix[type]
    }
  }

  // Merge in default cpid (if not provided)
  if (!hasCPID(parameters)) {
    parameters.cpid = targetVariantOptions.cpid
  }

  // Merge in default sourcesuffix (if not provided)
  if (
    !hasSourcesuffix(parameters)
  ) {
    parameters.sourcesuffix = targetVariantOptions.sourcesuffix.default
  }


  const querystring = stringifyQuery(parameters)

  // `stringifyQuery` encodes symbols, e.g. `:` into `%3A`. This leads to problems where the actual : is needed for tracking.
  // To test things out, we only convert back in the blue version.
  const fixedQuerystring = querystring.replace(/%3A/g, ':')

  if (
    targetVariant.includes('gold') || targetVariant.includes('platinum')
  ) {
    /*
    // Merge in default multicode (if not provided)
    if (!hasMulticode(parameters)) {
      parameters.multicode = targetVariantOptions.multicode
    }
    */
    let path
    if (targetVariant.includes('gold')) {
      path = '/Gold'
    }
    else if (targetVariant.includes('platinum')) {
      path = '/Platinum'
    }
    else path = ''

    const lowercaseKeys = (obj) =>
      Object.keys(obj).reduce((acc, key) => {
        acc[key.toLowerCase()] = obj[key];
        return acc;
      }, {});

    return `https://www.amex-card.at/eapply/Erfassung${path}/${lowercaseKeys(parameters).sourcesuffix}/${lowercaseKeys(parameters).cpid}?cpid=${lowercaseKeys(parameters).cpid}`
  }
  else return `${window.cta.baseURL}?${fixedQuerystring}`
}
